// @ts-nocheck
import { Game } from "./game-base";
import { Sound } from "../audio";
import { spellInfo, START_LOADOUT, equippedTitleId } from "../data";
import { spellIconInner } from "../spell-icons";
import { escapeHtml, playerNameHTML } from "../net-ui";
import { fmtInt, fmtDmg, statCountCell } from "../stat-count-up";

const PROFILE_STAT_ANIM_MS = 2500;

function statCell(label, spec) {
  return statCountCell(label, spec, PROFILE_STAT_ANIM_MS);
}

function spellDisplayName(spellId) {
  return spellInfo(spellId)?.name || spellId;
}

function spellDisplayColor(spellId) {
  return spellInfo(spellId)?.color || "#9b8fd6";
}

function spellSkillMeta(sp) {
  const parts = [];
  if (sp.damage) parts.push(`${fmtDmg(sp.damage)} dmg`);
  if (sp.matchWins) parts.push(`${fmtInt(sp.matchWins)} match win${sp.matchWins === 1 ? "" : "s"}`);
  if (sp.kills) parts.push(`${fmtInt(sp.kills)} kill${sp.kills === 1 ? "" : "s"}`);
  if (sp.picks) parts.push(`${fmtInt(sp.picks)} pick${sp.picks === 1 ? "" : "s"}`);
  return parts.length ? parts.join(" · ") : "No activity yet";
}

function renderProfileSkillRow(spellId, meta) {
  const color = spellDisplayColor(spellId);
  const row = document.createElement("div");
  row.className = "profile-skill-row";
  row.innerHTML = `
    <div class="profile-skill-icon spell-icon" style="background:${color}33;border:1px solid ${color}">${spellIconInner(spellId)}</div>
    <div class="profile-skill-copy">
      <div class="profile-skill-name">${escapeHtml(spellDisplayName(spellId))}</div>
      <div class="profile-skill-meta">${escapeHtml(meta)}</div>
    </div>`;
  return row;
}

function profileHandleInitial(handle) {
  return (handle || "?").slice(0, 1).toUpperCase();
}

function syncProfileAvatarElements(account) {
  const av = document.getElementById("profileAvatar");
  const fallback = document.getElementById("profileAvatarFallback");
  const chipAv = document.getElementById("profileChipAvatar");
  const guestIcon = document.getElementById("profileChipGuestIcon");

  if (account?.avatar) {
    if (av) {
      av.src = account.avatar;
      av.classList.remove("hidden");
    }
    if (fallback) fallback.classList.add("hidden");
    if (chipAv) {
      chipAv.src = account.avatar;
      chipAv.classList.remove("hidden");
    }
    if (guestIcon) guestIcon.classList.add("hidden");
    return;
  }

  const initial = profileHandleInitial(account?.handle);
  if (av) av.classList.add("hidden");
  if (fallback) {
    fallback.textContent = initial;
    fallback.classList.toggle("hidden", !account);
  }
  if (chipAv) chipAv.classList.add("hidden");
  if (guestIcon) {
    if (account) {
      guestIcon.textContent = initial;
      guestIcon.classList.remove("hidden");
    } else {
      guestIcon.textContent = "👤";
      guestIcon.classList.remove("hidden");
    }
  }
}

function defaultStarterSkills() {
  return Object.values(START_LOADOUT).map((spellId) => ({
    spellId,
    picks: 0,
    matchWins: 0,
    damage: 0,
    kills: 0,
    casts: 0,
  }));
}

function renderProfilePve(stats) {
  const el = document.getElementById("profilePve");
  if (!el) return;

  const bosses = Array.isArray(stats?.pveBosses) ? stats.pveBosses : [];
  if (!bosses.length) {
    el.classList.add("hidden");
    el.innerHTML = "";
    return;
  }

  el.classList.remove("hidden");
  el.innerHTML = "";

  const heading = document.createElement("h3");
  heading.className = "profile-pve-heading";
  heading.textContent = "PvE";
  el.appendChild(heading);

  const list = document.createElement("div");
  list.className = "profile-pve-list";
  for (const boss of bosses) {
    const row = document.createElement("div");
    row.className = "profile-pve-row";
    const name = boss.encountered ? (boss.displayName || boss.bossId || "???") : "???";
    const beaten = !!boss.beaten;
    row.innerHTML = `
      <span class="profile-pve-name${boss.encountered ? "" : " is-unknown"}">${escapeHtml(name)}</span>
      <span class="profile-pve-status${beaten ? " is-beaten" : " is-failed"}" aria-label="${beaten ? "Defeated" : "Not defeated"}">${beaten ? "✓" : "✗"}</span>`;
    list.appendChild(row);
  }
  el.appendChild(list);
}

function achievementModeLabel(scope, id) {
  if (id === "finding_obi_wan") return "";
  if (!scope) return "";
  if (scope === "pvp") return "PvP matchmaking";
  if (scope === "pve") return "PvE";
  if (scope === "matchmaking") return "Matchmaking";
  if (scope === "any") return "";
  return scope;
}

function buildAchievementRow(a, isUnlocked) {
  const row = document.createElement("div");
  row.className = "profile-achievement-row" + (isUnlocked ? " is-unlocked" : " is-locked");
  const when = a.unlockedAt ? new Date(a.unlockedAt).toLocaleDateString() : "";
  const scope = achievementModeLabel(a.modeScope || "", a.id || "");
  const titleBadge = a.title
    ? `<div class="profile-achievement-title-badge">Earns title: ${escapeHtml(a.title)}</div>`
    : "";
  const metaParts = [];
  if (scope) metaParts.push(scope);
  if (when) metaParts.push(when);
  const meta = metaParts.length
    ? `<div class="profile-achievement-meta">${metaParts.map((p) => escapeHtml(p)).join(" · ")}</div>`
    : "";
  row.innerHTML = `
    <div class="profile-achievement-name">${escapeHtml(a.name)}</div>
    ${a.desc ? `<div class="profile-achievement-desc">${escapeHtml(a.desc)}</div>` : ""}
    ${titleBadge}
    ${meta}`;
  return row;
}

function hasUnequippedUnlockedTitles(rows, account) {
  if (!account || equippedTitleId(account)) return false;
  return rows.some((a) => a.unlocked && a.title);
}

function appendAchievementSection(container, label, rows, unlocked) {
  if (!rows.length) return;
  const head = document.createElement("div");
  head.className = "achievements-section-head";
  head.textContent = `${label} (${rows.length})`;
  container.appendChild(head);
  for (const a of rows) container.appendChild(buildAchievementRow(a, unlocked));
}

function renderAchievementList(container, rows, filter = "all") {
  if (!container) return;
  container.innerHTML = "";
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) {
    container.innerHTML = '<p class="profile-stats-empty">No achievements configured.</p>';
    return;
  }
  const unlocked = list.filter((a) => a.unlocked);
  const locked = list.filter((a) => !a.unlocked);
  if (filter === "unlocked") {
    if (!unlocked.length) {
      container.innerHTML = '<p class="profile-stats-empty">No unlocked achievements yet.</p>';
      return;
    }
    appendAchievementSection(container, "Unlocked", unlocked, true);
    return;
  }
  if (filter === "locked") {
    if (!locked.length) {
      container.innerHTML = '<p class="profile-stats-empty">All achievements unlocked.</p>';
      return;
    }
    appendAchievementSection(container, "Locked", locked, false);
    return;
  }
  appendAchievementSection(container, "Unlocked", unlocked, true);
  appendAchievementSection(container, "Locked", locked, false);
}

function renderProfileSkills(stats) {
  const el = document.getElementById("profileSkills");
  if (!el) return;

  const starters = (Array.isArray(stats?.starters) && stats.starters.length)
    ? stats.starters
    : defaultStarterSkills();
  const top = Array.isArray(stats?.topSkills) ? stats.topSkills : [];

  el.classList.remove("hidden");
  el.innerHTML = "";

  const heading = document.createElement("h3");
  heading.className = "profile-skills-heading";
  heading.textContent = "Skills";
  el.appendChild(heading);

  const starterBlock = document.createElement("div");
  starterBlock.className = "profile-skill-block";
  const starterLabel = document.createElement("div");
  starterLabel.className = "profile-skill-label";
  starterLabel.textContent = "Starter skills";
  starterBlock.appendChild(starterLabel);
  for (const sp of starters) {
    if (!sp?.spellId) continue;
    starterBlock.appendChild(renderProfileSkillRow(sp.spellId, spellSkillMeta(sp)));
  }
  el.appendChild(starterBlock);

  if (top.length) {
    const block = document.createElement("div");
    block.className = "profile-skill-block";
    const label = document.createElement("div");
    label.className = "profile-skill-label";
    label.textContent = "Most performant";
    block.appendChild(label);
    for (const sp of top) {
      if (!sp?.spellId) continue;
      block.appendChild(renderProfileSkillRow(sp.spellId, spellSkillMeta(sp)));
    }
    el.appendChild(block);
  }
}

Game.prototype.isProfileExpanded = function () {
  const widget = document.getElementById("profileWidget");
  return !!(widget && !widget.classList.contains("hidden") && !widget.classList.contains("profile-widget-minimized"));
};

Game.prototype.renderProfileChip = function () {
  const sub = document.getElementById("profileChipSub");
  const signOutBtn = document.getElementById("profileSignOutBtn");
  if (!sub) return;

  if (signOutBtn) signOutBtn.classList.toggle("hidden", !this.account);

  if (this.account) {
    const preview = this._titlePreview;
    if (preview && preview.title) {
      sub.innerHTML = playerNameHTML(this.account.handle, preview.title, preview.titlePos);
    } else {
      sub.textContent = this.account.handle || "";
    }
    sub.classList.add("is-handle");
    syncProfileAvatarElements(this.account);
  } else {
    sub.textContent = this.authAvailable() ? "sign in" : "guest";
    sub.classList.remove("is-handle");
    syncProfileAvatarElements(null);
  }
};

Game.prototype.syncProfileWidget = function (visible) {
  const widget = document.getElementById("profileWidget");
  if (!widget) return;
  widget.classList.toggle("hidden", !visible);
  if (!visible) {
    this.closeProfile(false);
    return;
  }
  this.renderProfileChip();
};

Game.prototype.showProfileError = function (msg) {
  const el = document.getElementById("profileError");
  if (!el) return;
  if (msg) {
    el.textContent = msg;
    el.classList.remove("hidden");
  } else {
    el.textContent = "";
    el.classList.add("hidden");
  }
};

Game.prototype.renderProfileStats = function (stats) {
  const grid = document.getElementById("profileStats");
  const handle = document.getElementById("profileHandle");
  if (!grid || !this.account) return;

  if (handle) {
    const preview = this._titlePreview;
    if (preview && preview.title) {
      handle.innerHTML = playerNameHTML(this.account.handle, preview.title, preview.titlePos);
    } else {
      handle.textContent = this.account.handle || "";
    }
  }
  syncProfileAvatarElements(this.account);

  grid.innerHTML = "";
  const s = stats || {};

  grid.appendChild(statCell("Total games played", { kind: "int", to: s.games || 0 }));
  grid.appendChild(statCell("Playtime", { kind: "playtime", to: s.playtimeSecs || 0 }));
  grid.appendChild(statCell("Total damage done", { kind: "dmg", to: s.dmgDealt || 0 }));
  grid.appendChild(statCell("Total damage received", { kind: "dmg", to: s.dmgTaken || 0 }));
  grid.appendChild(statCell("Match wins", { kind: "int", to: s.matchWins || 0 }));
  grid.appendChild(statCell("Kills", { kind: "int", to: s.kills || 0 }));
  grid.appendChild(statCell("Deaths", { kind: "int", to: s.deaths || 0 }));
  grid.appendChild(statCell("Healing done", { kind: "dmg", to: s.healDone || 0 }));

  if (!(s.games || 0)) {
    const empty = document.createElement("p");
    empty.className = "profile-stats-empty";
    empty.textContent = "No recorded matches yet — finish a signed-in game to start tracking stats.";
    grid.appendChild(empty);
  }

  renderProfileSkills(s);
  renderProfilePve(s);
};

Game.prototype.refreshProfileTitles = function () {
  if (!this.account) return;
  if (this._profileTitles) {
    this.renderProfileTitles(this._profileTitles);
    return;
  }
  void this.fetchProfileTitles();
};

Game.prototype.renderAchievementsDialog = function () {
  const list = document.getElementById("achievementsDialogList");
  const summary = document.getElementById("achievementsDialogSummary");
  const filterEl = document.getElementById("achievementsDialogFilter");
  const guestEl = document.getElementById("achievementsDialogGuest");
  const titleHintEl = document.getElementById("achievementsDialogTitleHint");
  const signInBtn = document.getElementById("achievementsDialogSignIn");
  const rows = Array.isArray(this._achievementRows) ? this._achievementRows : [];
  const filter = this._achievementsFilter || "all";
  const signedIn = !!this.account;
  if (guestEl) guestEl.classList.toggle("hidden", signedIn);
  if (titleHintEl) {
    titleHintEl.classList.toggle("hidden", !signedIn || !hasUnequippedUnlockedTitles(rows, this.account));
  }
  if (signInBtn) signInBtn.classList.toggle("hidden", signedIn || !this.authAvailable());
  if (filterEl) {
    filterEl.classList.toggle("hidden", rows.length === 0);
    for (const btn of filterEl.querySelectorAll("[data-filter]")) {
      btn.classList.toggle("is-active", btn.getAttribute("data-filter") === filter);
    }
  }
  if (summary) {
    if (!rows.length) {
      summary.textContent = "No achievements configured.";
    } else if (signedIn) {
      const unlocked = rows.filter((a) => a.unlocked).length;
      summary.textContent = `${unlocked} / ${rows.length} unlocked`;
    } else {
      summary.textContent = `${rows.length} achievements · sign in to track progress`;
    }
  }
  renderAchievementList(list, rows, filter);
};

Game.prototype.openAchievementsDialog = function () {
  const backdrop = document.getElementById("achievementsDialog");
  if (!backdrop) return;
  this._achievementsFilter = "all";
  backdrop.classList.remove("hidden");
  if (!Array.isArray(this._achievementRows)) {
    const list = document.getElementById("achievementsDialogList");
    const summary = document.getElementById("achievementsDialogSummary");
    const guestEl = document.getElementById("achievementsDialogGuest");
    if (summary) summary.textContent = "";
    if (guestEl) guestEl.classList.add("hidden");
    const titleHintEl = document.getElementById("achievementsDialogTitleHint");
    if (titleHintEl) titleHintEl.classList.add("hidden");
    if (list) list.innerHTML = '<p class="profile-stats-empty">Loading achievements…</p>';
    void this.fetchAchievements();
    return;
  }
  this.renderAchievementsDialog();
};

Game.prototype.closeAchievementsDialog = function (sound = true) {
  const backdrop = document.getElementById("achievementsDialog");
  if (!backdrop || backdrop.classList.contains("hidden")) return;
  if (sound) Sound.ui();
  backdrop.classList.add("hidden");
};

Game.prototype.setupAchievementsDialog = function ($) {
  const backdrop = $("achievementsDialog");
  const close = () => { this.closeAchievementsDialog(); };
  if ($("achievementsDialogClose")) $("achievementsDialogClose").onclick = close;
  const signInBtn = $("achievementsDialogSignIn");
  if (signInBtn) {
    signInBtn.onclick = () => {
      Sound.ui();
      this.closeAchievementsDialog(false);
      if (this.authAvailable()) this.showSignIn();
    };
  }
  const profileBtn = $("achievementsDialogOpenProfile");
  if (profileBtn) {
    profileBtn.onclick = () => {
      Sound.ui();
      this.closeAchievementsDialog(false);
      this.openProfile();
    };
  }
  const filterEl = $("achievementsDialogFilter");
  if (filterEl) {
    this._achievementsFilter = "all";
    filterEl.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-filter]");
      if (!btn) return;
      const next = btn.getAttribute("data-filter") || "all";
      if (next === this._achievementsFilter) return;
      Sound.ui();
      this._achievementsFilter = next;
      this.renderAchievementsDialog();
    });
  }
  if (backdrop) {
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close();
    });
  }
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && backdrop && !backdrop.classList.contains("hidden")) {
      this.closeAchievementsDialog(false);
    }
  });
};

Game.prototype.fetchAchievements = async function () {
  if (!this.panelVisible("achievementsDialog")) return;
  const url = this.account ? "/users/me/achievements" : "/achievements";
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (this.account && res.status === 401) return;
    if (!res.ok) throw new Error("achievements unavailable");
    const data = await res.json();
    this._achievementRows = Array.isArray(data) ? data : [];
    if (!this.panelVisible("achievementsDialog")) return;
    this.renderAchievementsDialog();
  } catch {
    if (!this.panelVisible("achievementsDialog")) return;
    const list = document.getElementById("achievementsDialogList");
    if (list) list.innerHTML = '<p class="profile-stats-empty">Could not load achievements.</p>';
  }
};

Game.prototype.clearAchievementCache = function () {
  this._achievementRows = null;
};

Game.prototype.syncTitlePreview = function (equippedId, titles) {
  this._titlePreview = null;
  if (equippedId && Array.isArray(titles)) {
    const row = titles.find((t) => t.id === equippedId && t.unlocked);
    if (row) this._titlePreview = { title: row.title, titlePos: row.titlePos };
  }
  this.renderProfileChip();
  const handle = document.getElementById("profileHandle");
  if (!handle || !this.account) return;
  if (this._titlePreview?.title) {
    handle.innerHTML = playerNameHTML(this.account.handle, this._titlePreview.title, this._titlePreview.titlePos);
  } else {
    handle.textContent = this.account.handle || "";
  }
};

Game.prototype.renderProfileTitles = function (data) {
  const el = document.getElementById("profileTitles");
  if (!el || !this.account) return;
  const titles = Array.isArray(data?.titles) ? data.titles : [];
  const unlocked = titles.filter((t) => t.unlocked);
  el.innerHTML = "";
  if (!unlocked.length) {
    const head = document.createElement("p");
    head.className = "profile-titles-hint";
    head.textContent = "Earn hard achievements to unlock titles. Unlocked titles appear here as selectable pills.";
    el.appendChild(head);
    const pills = document.createElement("div");
    pills.className = "profile-title-pills";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "profile-title-pill" + (!data.equipped ? " is-active" : "");
    btn.textContent = "No title";
    btn.onclick = () => { Sound.ui(); void this.equipTitle(""); };
    pills.appendChild(btn);
    el.appendChild(pills);
    return;
  }
  const head = document.createElement("p");
  head.className = "profile-titles-hint";
  head.textContent = "Select a title to display in lobbies and matches.";
  el.appendChild(head);
  const pills = document.createElement("div");
  pills.className = "profile-title-pills";
  const addPill = (label, id, active) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "profile-title-pill" + (active ? " is-active" : "");
    btn.textContent = label;
    btn.onclick = () => { Sound.ui(); void this.equipTitle(id); };
    pills.appendChild(btn);
  };
  addPill("No title", "", !data.equipped);
  for (const t of unlocked) {
    addPill(t.title, t.id, data.equipped === t.id);
  }
  el.appendChild(pills);
};

Game.prototype.fetchProfileTitles = async function () {
  const el = document.getElementById("profileTitles");
  if (!el || !this.account) return;
  el.innerHTML = '<p class="profile-stats-empty">Loading titles…</p>';
  try {
    const res = await fetch("/users/me/titles", { cache: "no-store" });
    if (res.status === 401) return;
    if (!res.ok) throw new Error("titles unavailable");
    const data = await res.json();
    this._profileTitles = data;
    this.syncTitlePreview(data.equipped || "", data.titles);
    if (!this.isProfileExpanded()) return;
    this.renderProfileTitles(data);
  } catch {
    if (!this.isProfileExpanded()) return;
    el.innerHTML = '<p class="profile-stats-empty">Could not load titles.</p>';
  }
};

Game.prototype.equipTitle = async function (id) {
  if (!this.account) return;
  this.showProfileError("");
  try {
    const res = await fetch("/users/me/title", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: id || "" }),
    });
    if (!res.ok) throw new Error("save failed");
    const data = await res.json();
    this.account.equippedTitle = data.equippedTitle || "";
    if (this._profileTitles) this._profileTitles.equipped = this.account.equippedTitle;
    this.syncTitlePreview(this.account.equippedTitle, this._profileTitles?.titles);
    if (this.isProfileExpanded() && this._profileTitles) this.renderProfileTitles(this._profileTitles);
    if (this.panelVisible("achievementsDialog")) this.renderAchievementsDialog();
  } catch {
    this.showProfileError("Couldn't update title.");
  }
};

Game.prototype.fetchProfileStats = async function () {
  const grid = document.getElementById("profileStats");
  const skills = document.getElementById("profileSkills");
  const pve = document.getElementById("profilePve");
  if (grid) grid.innerHTML = '<p class="profile-stats-empty">Loading stats…</p>';
  if (skills) {
    skills.classList.add("hidden");
    skills.innerHTML = "";
  }
  if (pve) {
    pve.classList.add("hidden");
    pve.innerHTML = "";
  }
  this.showProfileError("");
  try {
    const res = await fetch("/users/me/stats", { cache: "no-store" });
    if (res.status === 401) {
      this.closeProfile();
      this.showSignIn();
      return;
    }
    if (!res.ok) throw new Error("stats unavailable");
    const data = await res.json();
    if (!this.isProfileExpanded()) return;
    this.renderProfileStats(data);
  } catch (e) {
    if (!this.isProfileExpanded()) return;
    this.showProfileError("Couldn't load lifetime stats.");
    if (grid) grid.innerHTML = "";
  }
};

Game.prototype.openProfile = function () {
  const widget = document.getElementById("profileWidget");
  const title = document.getElementById("profileExpandedTitle");
  const minBtn = document.getElementById("profileMinimize");
  if (!widget || !this.account) return;

  widget.classList.remove("profile-widget-minimized");
  if (title) title.classList.remove("hidden");
  if (minBtn) minBtn.classList.remove("hidden");
  this.refreshProfileTitles();
  this.renderProfileStats(null);
  void this.fetchProfileStats();
};

Game.prototype.closeProfile = function (sound = true) {
  const widget = document.getElementById("profileWidget");
  const title = document.getElementById("profileExpandedTitle");
  const minBtn = document.getElementById("profileMinimize");
  if (!widget || widget.classList.contains("hidden")) return;
  if (sound) Sound.ui();

  this.closeAchievementsDialog(false);
  widget.classList.add("profile-widget-minimized");
  if (title) title.classList.add("hidden");
  if (minBtn) minBtn.classList.add("hidden");
  this.showProfileError("");
};

Game.prototype.signOut = async function () {
  Sound.ui();
  try { await fetch("/auth/logout", { method: "POST" }); } catch (e) { /* ignore */ }
  this.account = null;
  this.activeGame = null;
  this._titlePreview = null;
  this._profileTitles = null;
  this.clearAchievementCache();
  this.homeGuestMode = false;
  this.closeAchievementsDialog(false);
  this.closeProfile(false);
  this.renderAccount();
};

Game.prototype.onProfileChipClick = function () {
  Sound.ui();
  if (!this.account) {
    if (this.authAvailable()) this.showSignIn();
    return;
  }
  if (this.isProfileExpanded()) return;
  this.openProfile();
};
